import { useState, useEffect, useContext } from 'react';
import { AuthContext } from "../../../auth/Auth";
import { useProjectSchemaDetails, useProjectState } from '../../../global-state/useProjectSchemas';
import { useMattoState } from '../../../MattoState';
import { Permissions, ProjectSchemeInterface } from 'ts-interfaces';
import { editProjectScheme } from '../../../v2/apis/projectsSchemeApi';

export const useProjectManagement = (selectedProject: ProjectSchemeInterface, permissions: Permissions) => {
  const { currentUser } = useContext(AuthContext);
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [editedProject, setEditedProject] = useState<ProjectSchemeInterface>({} as ProjectSchemeInterface);
  const setSnackbarMessage = useMattoState((state) => state.setSnackbarMessage);

  const projectId = selectedProject?.id;

  const { projectData, ownedProjects, sharedProjects, setBoards, setCollections } = useProjectSchemaDetails((state) => ({
    projectData: state.projectsData[projectId],
    ownedProjects: state.ownedProjects,
    sharedProjects: state.sharedProjects,
    setBoards: state.setBoards,
    setCollections: state.setCollections
  }));

  // Initialize the edited project state when the selected project changes
  useEffect(() => {
    if (selectedProject) {
      setEditedProject({ ...selectedProject });
    }
  }, [selectedProject]);

  useEffect(() => {
    if (selectedProject && currentUser?.uid) {
      useProjectSchemaDetails.getState().getProjectDetails(selectedProject, currentUser.uid, permissions);
    }
  }, [currentUser?.uid, selectedProject.id]);

  const handleInputChange = (field: string, value: any) => {
    setEditedProject(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSave = async () => {
    setIsSaving(true);
    setSnackbarMessage('Saving project details...');
    try {
      const updatedProject = await editProjectScheme(editedProject);
      useProjectSchemaDetails.getState().setProject(updatedProject.id, updatedProject);
      useProjectState.getState().setProjectData({ selectedProject: updatedProject });
      setIsEditing(false);
      setSnackbarMessage('Project details saved successfully');
    } catch (error) {
      setSnackbarMessage('Failed to save project details');
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    setEditedProject({ ...selectedProject });
    setIsEditing(false);
  };

  const handleEdit = () => {
    setIsEditing(true);
  };

  return {
    // State
    isEditing,
    isSaving,
    editedProject,
    projectData,
    ownedProjects,
    sharedProjects,
    
    // Actions
    handleInputChange,
    handleSave,
    handleCancel,
    handleEdit,
    setBoards,
    setCollections,
    
    // Utils
    currentUser,
    projectId,
    setSnackbarMessage
  };
};